import {Directive, ElementRef, Inject, Input, OnDestroy, OnInit, PLATFORM_ID, ViewContainerRef} from '@angular/core'
import {ConnectionPositionPair, FlexibleConnectedPositionStrategy, Overlay, OverlayRef} from '@angular/cdk/overlay'
import {auditTime, fromEvent, merge, Subject} from 'rxjs'
import {filter, map, take, takeUntil} from 'rxjs/operators'
import {TemplatePortal} from '@angular/cdk/portal'
import {isPlatformBrowser} from '@angular/common'
import {DropdownComponent} from './dropdown.component'

// Inspired by
// https://netbasal.com/advanced-angular-implementing-a-reusable-autocomplete-component-9908c2f04f5
// https://material.angular.io/components/autocomplete/overview
@Directive({
  selector: 'button[cftDropdown],cft-icon-button[cftDropdown],span[cftDropdown]',
  standalone: false,
})
export class DropdownDirective implements OnDestroy, OnInit {
  @Input() cftDropdown!: DropdownComponent

  private overlayRef?: OverlayRef
  private unsubscribe$ = new Subject<void>()

  constructor(
    private host: ElementRef<HTMLInputElement>,
    private vcr: ViewContainerRef,
    private overlay: Overlay,
    @Inject(PLATFORM_ID) private platformId: Record<string, unknown>,
  ) {}

  get origin(): HTMLInputElement {
    return this.host.nativeElement
  }

  ngOnInit(): void {
    merge(
      fromEvent<MouseEvent>(this.origin, 'click'),
      fromEvent<MouseEvent>(this.origin, 'auxclick'),
      fromEvent<MouseEvent>(this.origin, 'touchend'),
    )
      .pipe(
        map(e => e.stopPropagation()),
        auditTime(50), // prevent conflicting events (i.e touchend+click)
        takeUntil(this.unsubscribe$),
      )
      .subscribe(() => {
        this.toggleDropdown()
      })

    // address bug in ios where the dropdown shifts when keyboard is closed (and input loses focus)
    if (isPlatformBrowser(this.platformId) && window.visualViewport) {
      window.visualViewport.onresize = () => this.overlayRef?.updatePosition()
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  toggleDropdown(): void {
    if (this.overlayRef) {
      this.close()
    } else {
      this.open()
    }
  }

  private open() {
    const overlayRef = this.overlay.create({
      backdropClass: '',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      positionStrategy: this.getOverlayPosition(),
    })
    this.overlayRef = overlayRef

    // once the overlay is attached, start listening for closing events
    overlayRef
      .attachments()
      .pipe(take(1))
      .subscribe(_ => {
        // when the first closing event occurs... close the overlay
        merge(this.unsubscribe$, overlayClick(overlayRef, this.origin))
          .pipe(take(1))
          .subscribe(() => {
            this.close()
          })
      })

    const template = new TemplatePortal(this.cftDropdown.rootTemplate, this.vcr)
    overlayRef.attach(template)
  }

  private close() {
    this.overlayRef?.detach()
    this.overlayRef = undefined
  }

  private getOverlayPosition(): FlexibleConnectedPositionStrategy {
    // ordered list of preferred positions, from most to least desirable, on how to connect origin to overlay
    const positions = [
      new ConnectionPositionPair({originX: 'start', originY: 'bottom'}, {overlayX: 'start', overlayY: 'top'}),
      new ConnectionPositionPair({originX: 'end', originY: 'bottom'}, {overlayX: 'end', overlayY: 'top'}),
      new ConnectionPositionPair({originX: 'start', originY: 'top'}, {overlayX: 'start', overlayY: 'bottom'}),
      new ConnectionPositionPair({originX: 'end', originY: 'top'}, {overlayX: 'end', overlayY: 'bottom'}),
    ]

    return this.overlay
      .position()
      .flexibleConnectedTo(this.origin)
      .withPositions(positions)
      .withFlexibleDimensions(false)
      .withPush(false)
      .withDefaultOffsetY(1) //this is to account for the bottom borders of our input elements.
  }
}

function overlayClick(overlayRef: OverlayRef, origin: HTMLElement) {
  return merge(
    fromEvent<MouseEvent>(document, 'click'),
    fromEvent<MouseEvent>(document, 'auxclick'),
    fromEvent<MouseEvent>(document, 'touchend'),
  ).pipe(
    filter(event => {
      const clickTarget = event.target as HTMLElement
      const notOrigin = clickTarget !== origin
      //event.detail > 0: make sure it's an actual click and not for instance enter key pressed
      return notOrigin && event.detail > 0
    }),
    takeUntil(overlayRef.detachments()),
  )
}
